import { ReactNode, useMemo } from 'react';

// material-ui
import { CssBaseline, GlobalStyles, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, Theme, TypographyVariantsOptions } from '@mui/material/styles';

// project import
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';

// types
import { CustomShadowProps } from 'types/theme';
import { ThemeMode } from 'types/config';

// types
type ThemeCustomizationProps = {
  children: ReactNode;
};

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({ children }: ThemeCustomizationProps) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig();

  const theme: Theme = useMemo<Theme>(() => Palette(mode, presetColor), [mode, presetColor]);

  const themeTypography: TypographyVariantsOptions = useMemo<TypographyVariantsOptions>(
    () => Typography(mode, fontFamily, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, fontFamily]
  );
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => CustomShadows(theme), [theme]);

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: themeTypography
    }),
    [themeDirection, theme, themeTypography, themeCustomShadows]
  );

  const themes: Theme = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  const isDark = mode === ThemeMode.DARK;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            ':root': {
              colorScheme: mode,
              '--nv-bg': isDark ? '#0f1115' : '#f5f7fb',
              '--nv-surface': isDark ? '#17191f' : '#ffffff',
              '--nv-surface-soft': isDark ? '#20232b' : '#f8fafc',
              '--nv-surface-softer': isDark ? '#1b1e26' : '#f8fbff',
              '--nv-surface-hover': isDark ? '#282c35' : '#f1f5f9',
              '--nv-surface-raised': isDark ? '#1e222b' : '#fbfdff',
              '--nv-text-primary': isDark ? themes.palette.text.primary : '#111827',
              '--nv-text-strong': isDark ? themes.palette.text.primary : '#0f172a',
              '--nv-text-secondary': isDark ? themes.palette.text.secondary : '#475569',
              '--nv-text-muted': isDark ? '#94a3b8' : '#64748b',
              '--nv-text-subtle': isDark ? '#748095' : '#94a3b8',
              '--nv-border': isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.08)',
              '--nv-border-strong': isDark ? 'rgba(148, 163, 184, 0.32)' : 'rgba(15, 23, 42, 0.12)',
              '--nv-divider': isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.14)',
              '--nv-primary-soft': isDark ? 'rgba(59, 130, 246, 0.16)' : '#eff6ff',
              '--nv-primary-softer': isDark ? 'rgba(99, 102, 241, 0.16)' : '#eef2ff',
              '--nv-info-soft': isDark ? 'rgba(14, 165, 233, 0.16)' : '#f0f9ff',
              '--nv-success-soft': isDark ? 'rgba(34, 197, 94, 0.16)' : '#f0fdf4',
              '--nv-success-softer': isDark ? 'rgba(16, 185, 129, 0.16)' : '#ecfdf5',
              '--nv-warning-soft': isDark ? 'rgba(245, 158, 11, 0.16)' : '#fefce8',
              '--nv-danger-soft': isDark ? 'rgba(239, 68, 68, 0.16)' : '#fef2f2',
              '--nv-purple-soft': isDark ? 'rgba(139, 92, 246, 0.16)' : '#f5f3ff',
              '--nv-success-text': isDark ? '#86efac' : '#16a34a',
              '--nv-danger-text': isDark ? '#fca5a5' : '#dc2626',
              '--nv-warning-text': isDark ? '#facc15' : '#ca8a04',
              '--nv-info-text': isDark ? '#7dd3fc' : '#0369a1',
              // gold — the "top / standout" accent (e.g. the lead's strongest product)
              '--nv-gold-soft': isDark ? 'rgba(234, 179, 8, 0.14)' : '#fdf7e3',
              '--nv-gold-border': isDark ? 'rgba(234, 179, 8, 0.55)' : 'rgba(202, 138, 4, 0.45)',
              '--nv-gold-text': isDark ? '#fcd34d' : '#b45309',
              '--nv-shadow-color': isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(15, 23, 42, 0.12)'
            },
            body: {
              backgroundColor: 'var(--nv-bg)',
              color: 'var(--nv-text-primary)'
            },
            'input, textarea, select': {
              colorScheme: mode
            },
            '.apexcharts-text, .apexcharts-title-text, .apexcharts-legend-text': {
              fill: 'var(--nv-text-secondary) !important',
              color: 'var(--nv-text-secondary) !important'
            },
            '.apexcharts-gridline, .apexcharts-xaxis-tick, .apexcharts-yaxis-tick': {
              stroke: 'var(--nv-divider) !important'
            },
            '.bg-white, .bg-gray-50, .bg-slate-50': {
              backgroundColor: 'var(--nv-surface) !important'
            },
            '.bg-gray-100, .bg-slate-100': {
              backgroundColor: 'var(--nv-surface-soft) !important'
            },
            '.border-gray-100, .border-gray-200, .border-slate-100, .border-slate-200': {
              borderColor: 'var(--nv-border-strong) !important'
            },
            '.text-gray-900, .text-slate-900, .text-black': {
              color: 'var(--nv-text-primary) !important'
            },
            '.text-gray-800, .text-gray-700, .text-slate-800, .text-slate-700': {
              color: 'var(--nv-text-secondary) !important'
            },
            '.text-gray-600, .text-gray-500, .text-slate-600, .text-slate-500': {
              color: 'var(--nv-text-muted) !important'
            },
            '.hover\\:bg-gray-50:hover, .hover\\:bg-slate-50:hover': {
              backgroundColor: 'var(--nv-surface-hover) !important'
            },
            '.MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper, .MuiDrawer-paper': {
              backgroundImage: 'none',
              backgroundColor: 'var(--nv-surface)',
              color: 'var(--nv-text-primary)'
            },
            '.MuiTableCell-root': {
              borderColor: 'var(--nv-divider)'
            },
            '.MuiTableRow-root:hover': {
              backgroundColor: isDark ? 'rgba(148, 163, 184, 0.08)' : undefined
            },
            '.MuiOutlinedInput-root, .MuiInputBase-root': {
              color: 'var(--nv-text-primary)'
            },
            '.MuiInputLabel-root, .MuiFormHelperText-root': {
              color: 'var(--nv-text-muted)'
            },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--nv-border-strong)'
            },
            '.simplebar-scrollbar:before': {
              backgroundColor: isDark ? 'rgba(148, 163, 184, 0.45)' : undefined
            }
          }}
        />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
